import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import { baseUrl } from "../config";
import { DateIRLToErache } from "../utils/dateConverters";
import { EventDetailContent } from "./DetailContent";

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventDeleteDialog, setEventDeleteDialog] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${baseUrl}/events/${id}/details`, { withCredentials: true })
      .then((res) => {
        setData(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message);
        setData(null);
        setLoading(false);
      });
  }, [id]);

  const handleEdit = () => {
    if (!data) return;
    navigate(`/edit-event/${data.id}`);
  };

  const handleDeleteClick = () => {
    if (!data) return;
    setEventDeleteDialog({ mode: "simple", event: data });
  };

  const handleConfirmEventDelete = async () => {
    if (!eventDeleteDialog?.event) return;
    const { event, mode } = eventDeleteDialog;
    try {
      const config = { withCredentials: true };
      if (mode === "periods") config.params = { confirmDeactivatePeriods: "1" };
      await axios.delete(`${baseUrl}/events/${event.id}`, config);
      setEventDeleteDialog(null);
      navigate("/list");
    } catch (err) {
      if (
        mode === "simple" &&
        err.response?.status === 409 &&
        err.response?.data?.periodIds?.length > 0
      ) {
        setEventDeleteDialog({
          event,
          mode: "periods",
          periodIds: err.response.data.periodIds,
          message:
            err.response.data.message ||
            "Cet événement est lié à des périodes. Elles seront désactivées si vous confirmez.",
        });
        return;
      }
      setEventDeleteDialog(null);
    }
  };

  const onOpenDetail = ({ type, id: detailId }) => {
    navigate(type === "event" ? `/events/${detailId}` : `/periods/${detailId}`);
  };

  const canEdit = data?.canEdit;
  const canDelete = data?.canDelete;

  if (loading) return <Box sx={{ p: 2 }}><Typography variant="body2">Chargement…</Typography></Box>;
  if (error) return <Box sx={{ p: 2 }}><Typography variant="body2" color="error">{error}</Typography></Box>;
  if (!data) return null;

  return (
    <>
      <Box sx={{ maxWidth: 720, mx: "auto", p: 2 }}>
        <EventDetailContent
          data={data}
          onOpenDetail={onOpenDetail}
          DateIRLToErache={DateIRLToErache}
        />
        {canEdit && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider", display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={handleEdit}>
              Éditer
            </Button>
            {canDelete && (
              <Button variant="outlined" color="error" onClick={handleDeleteClick}>
                Supprimer
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Dialog open={!!eventDeleteDialog} onClose={() => setEventDeleteDialog(null)}>
        <DialogTitle>
          {eventDeleteDialog?.mode === "periods"
            ? "Événement lié à des périodes"
            : "Supprimer l'évènement"}
        </DialogTitle>
        <DialogContent>
          {eventDeleteDialog?.mode === "periods" ? (
            <>
              <p>{eventDeleteDialog?.message}</p>
              <p>Périodes concernées : {eventDeleteDialog?.periodIds?.length ?? 0}</p>
            </>
          ) : (
            <p>
              Êtes-vous sûr de vouloir supprimer « {eventDeleteDialog?.event?.name} » ? Cette
              action est définitive.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDeleteDialog(null)}>Annuler</Button>
          <Button onClick={handleConfirmEventDelete} variant="contained">
            Confirmer la suppression
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
